import { augmentRelMissingEndpoint } from "./enrichmentMissingEndpoint";
import { compactCatalogClaim, compactRelationshipClaim } from "./enrichmentReviewQueue";

/** Internal / in-progress actions — not shown on entity audit timelines. */
export const ENRICHMENT_AUDIT_ACTIONS_SKIP = new Set([
  "approve_proposed_relationship_started",
  "approve_interface_suggestion_started",
  "approve_proposed_relationship_failed",
  "approve_interface_suggestion_failed",
  "clear_stale_catalog_creating",
  "clear_stale_relationship_approving",
  "clear_stale_in_progress_outcomes",
  "wire_catalog_endpoints_from_candidates",
  "revise_proposed_relationship",
  "revise_catalog_entity",
  "revise_description",
  "mark_review_complete",
  "auto_approve_anchor_roster",
  "infer_description_already_applied",
  "rename_review_item",
  "enrichment_roster_entity_type_override",
  "resume_enrichment_review",
]);

const ACTION_LABELS = {
  approve_proposed_relationship: "Accepted relationship suggestion",
  discard_proposed_relationship: "Discarded relationship suggestion",
  discard_proposed_relationships_metamodel_invalidation:
    "Discarded invalid relationship suggestions",
  adjust_proposed_relationships_metamodel:
    "Updated relationship suggestions for catalog rules",
  approve_interface_suggestion: "Accepted interface suggestion",
  discard_interface_suggestion: "Discarded interface suggestion",
  approve_enrichment_roster_entity: "Accepted roster entity",
  discard_enrichment_roster_entity: "Discarded roster entity",
  apply_description: "Updated application description",
  create_catalog_entity: "Created catalog entity",
  use_existing_catalog_entity: "Matched to existing catalog entity",
  confirm_resolution_match: "Confirmed catalog match",
  keep_new_resolution_match: "Kept as new entity",
  discard_resolution_match: "Discarded potential match",
  set_entity_type_override: "Updated entity type",
};

function relPeerLabelForAudit(rel) {
  const r = augmentRelMissingEndpoint(rel);
  return (
    String(r?.missing_surface_name || "").trim() ||
    String(r?.surface_target_name || "").trim() ||
    String(r?.surface_source_name || "").trim() ||
    String(r?.surface_other_application || "").trim() ||
    String(r?.surface_child_application || "").trim() ||
    ""
  );
}

/** Context fields for relationship approve/discard audit rows. */
export function relationshipAuditFields(rel, anchorName) {
  const relEff = augmentRelMissingEndpoint(rel);
  const anchor =
    String(anchorName || relEff?.anchor_name || "Application").trim() ||
    "Application";
  const peer = relPeerLabelForAudit(relEff);
  const claim = compactRelationshipClaim(anchor, relEff);
  return {
    entity_name: peer || undefined,
    relationship_type: String(relEff?.relationship_type || "").trim() || undefined,
    claim,
  };
}

/** Context fields for interface / catalog stub audit rows. */
export function catalogAuditFields(row) {
  const name = String(row?.suggested_name || row?.interface_name || "").trim();
  return {
    entity_name: name || undefined,
    entity_type: "interface",
    claim: compactCatalogClaim(row),
  };
}

export function shouldRecordEnrichmentAuditEvent(auditEvent) {
  if (!auditEvent || typeof auditEvent !== "object") return false;
  const action = String(auditEvent.action || "").trim();
  if (!action) return false;
  return !ENRICHMENT_AUDIT_ACTIONS_SKIP.has(action);
}

export function isEnrichmentAuditEventVisible(event) {
  if (!shouldRecordEnrichmentAuditEvent(event)) {
    const action = String(event?.action || "").trim();
    if (action) return false;
    const summary = String(event?.summary || "").toLowerCase();
    if (/\bstarted\b/.test(summary) || /\bfailed\b/.test(summary)) return false;
    if (/^clear stale\b/.test(summary)) return false;
    return false;
  }
  return true;
}

export function enrichmentAuditLabel(event) {
  if (!event || typeof event !== "object") return "Review action";
  const action = String(event.action || "").trim();

  if (action === "discard_proposed_relationship") {
    const n = Array.isArray(event.proposed_keys) ? event.proposed_keys.length : 0;
    return n > 1
      ? `Discarded ${n} relationship suggestions`
      : ACTION_LABELS.discard_proposed_relationship;
  }
  if (action === "discard_proposed_relationships_metamodel_invalidation") {
    const n = Array.isArray(event.indices) ? event.indices.length : 0;
    return n > 1
      ? `Discarded ${n} invalid relationship suggestions`
      : ACTION_LABELS.discard_proposed_relationships_metamodel_invalidation;
  }
  if (action === "approve_proposed_relationship") {
    return ACTION_LABELS.approve_proposed_relationship;
  }
  if (action === "discard_interface_suggestion") {
    return ACTION_LABELS.discard_interface_suggestion;
  }
  if (action === "approve_interface_suggestion") {
    return ACTION_LABELS.approve_interface_suggestion;
  }

  if (ACTION_LABELS[action]) return ACTION_LABELS[action];

  const legacy = String(event.summary || "").trim();
  if (legacy) {
    const head = legacy.split(" — ")[0].trim();
    if (head && !/\bstarted\b/i.test(head)) return head;
  }
  return action.replace(/_/g, " ") || "Review action";
}

export function enrichmentAuditDetail(event) {
  if (!event || typeof event !== "object") return "";

  const claim = String(event.claim || "").trim();
  if (claim) return claim;

  const parts = [];
  const name = String(
    event.entity_name || event.surface_name || event.interface_name || ""
  ).trim();
  const type = String(event.entity_type || "").trim();
  const relType = String(event.relationship_type || "").trim();

  if (name && relType) {
    parts.push(`${name} (${relType})`);
  } else if (name) {
    parts.push(name);
  } else if (relType) {
    parts.push(relType);
  }

  if (type && !relType) parts.push(`(${type})`);

  const id = String(event.entity_id || "").trim();
  if (id && !name) parts.push(id.length > 12 ? `${id.slice(0, 8)}…` : id);

  if (event.alias_added) {
    parts.push(`alias: ${event.alias_added}`);
  }

  return parts.join(" · ").trim();
}

function buildAuditSummary(event) {
  const label = enrichmentAuditLabel(event);
  const detail = enrichmentAuditDetail(event);
  return detail ? `${label} — ${detail}` : label;
}

/** Append an audit row to preview for live UI (job result is updated separately via API). */
export function appendEnrichmentReviewAudit(preview, auditEvent) {
  if (!preview || !auditEvent || typeof auditEvent !== "object") return preview;
  if (!shouldRecordEnrichmentAuditEvent(auditEvent)) return preview;

  const ev = {
    ...auditEvent,
    at: auditEvent.at || new Date().toISOString(),
    summary: buildAuditSummary(auditEvent),
  };

  const existing = Array.isArray(preview.enrichment_review_audit)
    ? preview.enrichment_review_audit
    : [];
  return {
    ...preview,
    enrichment_review_audit: [...existing, ev],
  };
}

/** Merge persisted job audits into preview when opening a review session. */
export function mergeJobAuditsIntoPreview(preview, jobDoc) {
  if (!preview) return preview;
  const docAudits = Array.isArray(jobDoc?.enrichment_review_audit)
    ? jobDoc.enrichment_review_audit
    : [];
  const previewAudits = Array.isArray(preview.enrichment_review_audit)
    ? preview.enrichment_review_audit
    : [];
  const merged =
    docAudits.length >= previewAudits.length ? docAudits : previewAudits;
  const enrichment_review_audit = merged.filter(isEnrichmentAuditEventVisible);
  if (
    enrichment_review_audit.length === previewAudits.length &&
    enrichment_review_audit === previewAudits
  ) {
    return preview;
  }
  return { ...preview, enrichment_review_audit };
}
